import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { currentUserService } from '../services/currentUserService';
import type { RoleName } from '../api-client/models/RoleName';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: RoleName[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles }) => {
  const location = useLocation();
  const isAuthenticated = authService.isAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<RoleName[]>([]);

  // Загружаем роли пользователя
  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    // Если роли не требуются, не загружаем пользователя
    if (!requiredRoles || requiredRoles.length === 0) {
      setIsLoading(false);
      return;
    }

    const loadUserRoles = async () => {
      try {
        const roles = await currentUserService.getUserRoles();
        setUserRoles(roles);
      } catch (error) {
        console.error('Failed to load user roles:', error);
        setUserRoles([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserRoles();
  }, [isAuthenticated, requiredRoles]);

  // Если не авторизован - редирект на логин
  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    authService.setReturnUrl(currentPath);
    return <Navigate to={`/login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  // Если загружаем роли - показываем индикатор загрузки
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Загрузка...</span>
        </div>
      </div>
    );
  }

  // Если указаны требуемые роли - проверяем их
  if (requiredRoles && requiredRoles.length > 0) {
    const hasAccess = requiredRoles.some((role) => userRoles.includes(role));

    if (!hasAccess) {
      // Если нет доступа - редирект на профиль
      return <Navigate to="/profile" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
